// ============================================================
//  src/pages/LandingPage.js
//  CommandKit — WhatsApp Bot Platform landing page
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

// ── Animated chat demo ────────────────────────────────────────
const CHAT_SCRIPT = [
  { from: 'user',  text: 'Hi, I want to buy a data plan', delay: 400 },
  { from: 'bot',   text: 'Welcome! 👋 Please choose a plan:', delay: 900,
    buttons: ['500MB — ₦200', '2GB — ₦500', '5GB — ₦1,000'] },
  { from: 'user',  text: '2GB — ₦500', delay: 700 },
  { from: 'bot',   text: '✅ Order confirmed! Your 2GB data will be active in seconds. Reference: #BT8821', delay: 1000 },
];

function ChatDemo() {
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState(false);
  const chatBodyRef = useRef(null);

  useEffect(() => {
    let i = 0;
    function next() {
      if (i >= CHAT_SCRIPT.length) {
        // Replay after pause
        setTimeout(() => { setShown([]); i = 0; next(); }, 4000);
        return;
      }
      const msg = CHAT_SCRIPT[i];
      setTyping(msg.from === 'bot');
      setTimeout(() => {
        setTyping(false);
        setShown(prev => [...prev, msg]);
        i++;
        setTimeout(next, msg.delay);
      }, msg.from === 'bot' ? 1200 : 300);
    }
    const t = setTimeout(next, 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const el = chatBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [shown, typing]);

  return (
    <div className="chat-phone">
      <div className="chat-header">
        <div className="chat-avatar">C</div>
        <div>
          <div className="chat-name">CommandKit Assistant</div>
          <div className="chat-status">● Online</div>
        </div>
      </div>
      <div className="chat-body" ref={chatBodyRef}>
        {shown.map((msg, idx) => (
          <div key={idx} className={`chat-bubble-wrap ${msg.from}`}>
            <div className={`chat-bubble ${msg.from}`}>
              <span>{msg.text}</span>
              {msg.buttons && (
                <div className="chat-btns">
                  {msg.buttons.map(b => (
                    <button key={b} className="chat-btn">{b}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {typing && (
          <div className="chat-bubble-wrap bot">
            <div className="chat-bubble bot typing-bubble">
              <span className="dot" /><span className="dot" /><span className="dot" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Feature card ──────────────────────────────────────────────
function Feature({ icon, title, desc }) {
  return (
    <div className="feat-card">
      <div className="feat-icon">{icon}</div>
      <div className="feat-title">{title}</div>
      <div className="feat-desc">{desc}</div>
    </div>
  );
}

// ── Stat ──────────────────────────────────────────────────────
function Stat({ value, label }) {
  return (
    <div className="stat-item">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// ── Step ──────────────────────────────────────────────────────
function Step({ n, title, desc }) {
  return (
    <div className="step-item">
      <div className="step-num">{n}</div>
      <div>
        <div className="step-title">{title}</div>
        <div className="step-desc">{desc}</div>
      </div>
    </div>
  );
}

// ── Testimonial ──────────────────────────────────────────────
function Testimonial({ quote, name, role, initials }) {
  return (
    <div className="test-card">
      <div className="test-stars">★★★★★</div>
      <p className="test-quote">"{quote}"</p>
      <div className="test-person">
        <div className="test-avatar">{initials}</div>
        <div>
          <div className="test-name">{name}</div>
          <div className="test-role">{role}</div>
        </div>
      </div>
    </div>
  );
}

// ── FAQ item ──────────────────────────────────────────────────
function FaqItem({ q, a, isOpen, onClick }) {
  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-q" onClick={onClick}>
        <span>{q}</span>
        <span className="faq-icon">{isOpen ? '−' : '+'}</span>
      </button>
      <div className="faq-a-wrap">
        <p className="faq-a">{a}</p>
      </div>
    </div>
  );
}

// ── Main landing page ─────────────────────────────────────────
const FAQS = [
  {
    q: 'Do I need a Meta Business / WhatsApp Business API account?',
    a: 'You need a Meta Business account, which is free to create. CommandKit walks you through connecting your WhatsApp number to the official WhatsApp Business Platform during onboarding — no developer or technical setup required.',
  },
  {
    q: 'Will my customers know they\'re talking to a bot?',
    a: 'That\'s up to you. Most CommandKit bots blend automated replies with a human handoff option, so customers get instant answers but can always reach a real person when needed.',
  },
  {
    q: 'What happens if I go over my message limit on the Free plan?',
    a: 'Your bot keeps running, but additional messages pause until the next day or until you upgrade. We\'ll notify you in-app before you hit the limit so there are no surprises.',
  },
  {
    q: 'Can I cancel or downgrade anytime?',
    a: 'Yes. There are no contracts or lock-in periods. Cancel or switch plans whenever you like from your dashboard — changes apply from your next billing cycle.',
  },
  {
    q: 'Is my customer data safe?',
    a: 'All messages run through Meta\'s official WhatsApp Business Platform with end-to-end encrypted transport, and your account data is encrypted at rest. We never sell or share your customer data.',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="lp-root">

      {/* ── Navbar ── */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-logo">
            <span className="lp-logo-mark">C</span>
            <span className="lp-logo-text">CommandKit</span>
          </div>
          <div className={`lp-nav-links ${menuOpen ? 'open' : ''}`}>
            <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#how" onClick={() => setMenuOpen(false)}>How it works</a>
            <a href="#pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
            <button className="lp-btn-outline" onClick={() => navigate('/login')}>Sign in</button>
            <button className="lp-btn-primary" onClick={() => navigate('/register')}>Get started free</button>
          </div>
          <button className="lp-hamburger" onClick={() => setMenuOpen(o => !o)}>
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-copy">
            <div className="lp-eyebrow">WhatsApp automation, finally simple</div>
            <h1 className="lp-h1">
              Build bots that<br />
              <span className="lp-h1-accent">actually sell</span>
            </h1>
            <p className="lp-hero-sub">
              CommandKit turns your WhatsApp number into a 24/7 sales machine —
              answering customer questions, taking orders, and confirming payments
              automatically, even while you sleep. No coding required, yet powerful
              enough for developers who want full control.
            </p>
            <div className="lp-hero-ctas">
              <button className="lp-btn-primary lp-btn-lg" onClick={() => navigate('/register')}>
                Start for free →
              </button>
              <button className="lp-btn-ghost" onClick={() => navigate('/login')}>
                I have an account
              </button>
            </div>
            <div className="lp-hero-trust">
              <span>✓ No credit card required</span>
              <span>✓ 2 bots free forever</span>
              <span>✓ Live in under 5 minutes</span>
            </div>
            <div className="lp-hero-badge-row">
              <span className="lp-meta-badge">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 2 2 7v6c0 5 4.5 8.5 10 9 5.5-.5 10-4 10-9V7l-10-5z"/></svg>
                Built on the official WhatsApp Business Platform
              </span>
            </div>
          </div>
          <div className="lp-hero-demo">
            <ChatDemo />
          </div>
        </div>
        <div className="lp-hero-blob" aria-hidden="true" />
      </section>

      {/* ── Stats strip ── */}
      <section className="lp-stats">
        <div className="lp-inner">
          <Stat value="10,000+" label="Messages automated daily" />
          <div className="stat-divider" />
          <Stat value="500+"     label="Active bots deployed" />
          <div className="stat-divider" />
          <Stat value="99.9%"    label="Uptime guaranteed" />
          <div className="stat-divider" />
          <Stat value="< 5min"   label="Time to first bot" />
        </div>
      </section>

      {/* ── Features ── */}
      <section className="lp-section" id="features">
        <div className="lp-inner">
          <div className="lp-section-hdr">
            <div className="lp-tag">Features</div>
            <h2 className="lp-h2">Everything your business needs</h2>
            <p className="lp-section-sub">From simple auto-replies to full sales funnels — build it all on CommandKit.</p>
          </div>
          <div className="lp-features-grid">
            <Feature
              icon="⚡"
              title="Visual Flow Builder"
              desc="Drag-and-drop WhatsApp Flows that guide customers through purchases, bookings, and support — without writing a line of code."
            />
            <Feature
              icon="📢"
              title="Smart Broadcasts"
              desc="Send targeted messages to all your bot users at once. Announce products, run promos, or deliver order updates instantly."
            />
            <Feature
              icon="🕐"
              title="Scheduled Messages"
              desc="Set messages to send at the perfect time — morning reminders, delivery confirmations, follow-ups. Your bot works while you sleep."
            />
            <Feature
              icon="👥"
              title="Team Collaboration"
              desc="Invite your team to manage bots together. Set roles and permissions so everyone has exactly the access they need."
            />
            <Feature
              icon="🌐"
              title="Community Support"
              desc="Ask questions, share tips, and learn from other bot builders in the built-in community — no external forum needed."
            />
            <Feature
              icon="📊"
              title="Real-time Analytics"
              desc="See who's talking to your bot, what they're clicking, and how your automations are performing — all in one dashboard."
            />
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="lp-section lp-section-dark" id="how">
        <div className="lp-inner">
          <div className="lp-section-hdr">
            <div className="lp-tag lp-tag-light">How it works</div>
            <h2 className="lp-h2 lp-h2-light">Three steps to your first bot</h2>
          </div>
          <div className="lp-steps">
            <Step
              n="01"
              title="Connect your WhatsApp"
              desc="Link your Meta Business account and WhatsApp phone number. Takes 3 minutes, no developer needed."
            />
            <div className="step-connector" aria-hidden="true" />
            <Step
              n="02"
              title="Build your commands & flows"
              desc="Create keyword triggers, menu flows, and automated replies using our visual editor."
            />
            <div className="step-connector" aria-hidden="true" />
            <Step
              n="03"
              title="Go live & grow"
              desc="Activate your bot and watch it handle customer conversations 24/7 while you focus on your business."
            />
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="lp-section" id="testimonials">
        <div className="lp-inner">
          <div className="lp-section-hdr">
            <div className="lp-tag">Real businesses, real results</div>
            <h2 className="lp-h2">Built for how Nigerian businesses actually sell</h2>
            <p className="lp-section-sub">From data resellers to boutiques, here's what running on CommandKit looks like day to day.</p>
          </div>
          <div className="lp-test-grid">
            <Testimonial
              initials="AO"
              name="Adaeze O."
              role="Data & airtime reseller, Lagos"
              quote="I used to reply to over 200 messages a day by hand. Now my bot handles plan selection and confirms payment instantly — I only step in for disputes."
            />
            <Testimonial
              initials="TI"
              name="Tunde I."
              role="Boutique owner, Abuja"
              quote="The broadcast feature alone paid for Pro. I sent one restock alert to 800 customers and sold out in two hours."
            />
            <Testimonial
              initials="FB"
              name="Fatima B."
              role="Logistics startup, Kano"
              quote="Setup took me less than 10 minutes with zero coding experience. Delivery status updates now go out automatically instead of my staff calling each customer."
            />
          </div>
        </div>
      </section>


      <section className="lp-section" id="pricing">
        <div className="lp-inner">
          <div className="lp-section-hdr">
            <div className="lp-tag">Pricing</div>
            <h2 className="lp-h2">Start free, scale when ready</h2>
            <p className="lp-section-sub">No hidden fees. Cancel anytime.</p>
          </div>
          <div className="lp-pricing-grid">

            <div className="lp-plan">
              <div className="lp-plan-name">Free</div>
              <div className="lp-plan-price">₦0<span>/forever</span></div>
              <ul className="lp-plan-features">
                <li>✓ 2 bots</li>
                <li>✓ Basic flows</li>
                <li>✓ Community access</li>
                <li>✓ 100 messages/day</li>
              </ul>
              <button className="lp-btn-outline lp-plan-btn" onClick={() => navigate('/register')}>
                Get started free
              </button>
            </div>

            <div className="lp-plan lp-plan-featured">
              <div className="lp-plan-badge">Most popular</div>
              <div className="lp-plan-name">Pro</div>
              <div className="lp-plan-price">₦5,000<span>/30 days</span></div>
              <ul className="lp-plan-features">
                <li>✓ 5 bots</li>
                <li>✓ WhatsApp Flows</li>
                <li>✓ Scheduled messages</li>
                <li>✓ Broadcast to all users</li>
                <li>✓ Priority support</li>
              </ul>
              <button className="lp-btn-primary lp-plan-btn" onClick={() => navigate('/register')}>
                Start Pro trial
              </button>
            </div>

            <div className="lp-plan">
              <div className="lp-plan-name">Business</div>
              <div className="lp-plan-price">₦15,000<span>/30 days</span></div>
              <ul className="lp-plan-features">
                <li>✓ Unlimited bots</li>
                <li>✓ Team collaborators</li>
                <li>✓ Advanced analytics</li>
                <li>✓ Dedicated support</li>
                <li>✓ Custom integrations</li>
              </ul>
              <button className="lp-btn-outline lp-plan-btn" onClick={() => navigate('/register')}>
                Contact sales
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-section lp-section-dark" id="faq">
        <div className="lp-inner lp-faq-inner">
          <div className="lp-section-hdr">
            <div className="lp-tag lp-tag-light">FAQ</div>
            <h2 className="lp-h2 lp-h2-light">Questions, answered</h2>
          </div>
          <div className="faq-list">
            {FAQS.map((f, i) => (
              <FaqItem
                key={f.q}
                q={f.q}
                a={f.a}
                isOpen={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="lp-cta">
        <div className="lp-inner lp-cta-inner">
          <h2 className="lp-cta-h2">Your first bot is one click away</h2>
          <p className="lp-cta-sub">Join hundreds of Nigerian businesses already automating their WhatsApp.</p>
          <button className="lp-btn-primary lp-btn-lg lp-btn-white" onClick={() => navigate('/register')}>
            Create your free account →
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-inner lp-footer-inner">
          <div className="lp-logo">
            <span className="lp-logo-mark">C</span>
            <span className="lp-logo-text" style={{ color: '#9CA3AF' }}>CommandKit</span>
          </div>
          <div className="lp-footer-links">
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
            <a href="#pricing">Pricing</a>
            <a href="/login">Sign in</a>
          </div>
          <div className="lp-footer-copy">
            © {new Date().getFullYear()} CommandKit. Built for Nigerian businesses.
          </div>
        </div>
      </footer>

      {/* ── Mobile sticky CTA ── */}
      <div className="lp-sticky-cta">
        <button className="lp-btn-primary" onClick={() => navigate('/register')}>
          Start for free →
        </button>
      </div>

    </div>
  );
}